import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { BACKEND_URL, inr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Upload, Loader2, Sparkles, Search, Check } from "lucide-react";

const PRODUCT_TYPES = ["dress","top","bottom","ethnic","jewelry","bag","shoes","accessories","other"];

const empty = {
  title:"", description:"", price:0, mrp:0, category:"Dresses", brand:"Stylefire",
  sizes:[], colors:[], images:[], stock:10, rating:4.2,
  is_trending:false, is_bestseller:false, is_new:true,
  try_on_enabled:false, product_type:"other", try_on_asset:"", recommended_product_ids:[],
};

export default function AdminProductEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [imgUrl, setImgUrl] = useState("");
  const [sizeInput, setSizeInput] = useState("");
  const [colorInput, setColorInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/categories").then(r => setCats(r.data));
    api.get("/brands").then(r => setBrands(r.data));
    if (id) api.get(`/products/${id}`).then(r => setForm(r.data));
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const num = (k, v) => set(k, Number(v));

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    payload.price = Number(payload.price); payload.mrp = Number(payload.mrp); payload.stock = Number(payload.stock);
    try {
      if (id) await api.put(`/products/${id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved");
      nav("/admin/products");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const addImage = () => { if (imgUrl) { set("images", [...form.images, imgUrl]); setImgUrl(""); } };
  const rmImage = (i) => set("images", form.images.filter((_,idx)=>idx!==i));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${BACKEND_URL}${data.url}`);
      }
      set("images", [...form.images, ...uploaded]);
      toast.success(`${uploaded.length} image${uploaded.length>1?"s":""} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="font-display text-3xl font-bold mb-6">{id?"Edit Product":"New Product"}</h1>
      <form onSubmit={submit} className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4">
        <Input label="Title" value={form.title} onChange={v=>set("title",v)} testid="prod-title"/>
        <Textarea label="Description" value={form.description} onChange={v=>set("description",v)} testid="prod-desc"/>
        <div className="grid sm:grid-cols-3 gap-4">
          <Input label="Price (₹)" type="number" value={form.price} onChange={v=>num("price",v)} testid="prod-price"/>
          <Input label="MRP (₹)" type="number" value={form.mrp} onChange={v=>num("mrp",v)} testid="prod-mrp"/>
          <Input label="Stock" type="number" value={form.stock} onChange={v=>num("stock",v)} testid="prod-stock"/>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="block text-xs font-semibold uppercase text-slate-500 mb-1">Category</span>
            <select data-testid="prod-cat" value={form.category} onChange={e=>set("category",e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
              {cats.map(c => <option key={c.category_id} value={c.name}>{c.name}</option>)}
            </select></label>
          <label className="block"><span className="block text-xs font-semibold uppercase text-slate-500 mb-1">Brand</span>
            <select data-testid="prod-brand" value={form.brand} onChange={e=>set("brand",e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
              {brands.map(b => <option key={b.brand_id} value={b.name}>{b.name}</option>)}
            </select></label>
        </div>

        <Pills label="Sizes" items={form.sizes} input={sizeInput} setInput={setSizeInput} add={()=>{ if(sizeInput) { set("sizes",[...form.sizes,sizeInput]); setSizeInput(""); } }} remove={(i)=>set("sizes",form.sizes.filter((_,x)=>x!==i))} testid="prod-size"/>
        <Pills label="Colors" items={form.colors} input={colorInput} setInput={setColorInput} add={()=>{ if(colorInput) { set("colors",[...form.colors,colorInput]); setColorInput(""); } }} remove={(i)=>set("colors",form.colors.filter((_,x)=>x!==i))} testid="prod-color"/>

        <div>
          <span className="block text-xs font-semibold uppercase text-slate-500 mb-2">Product Images</span>

          {/* Direct upload */}
          <div
            onClick={() => fileRef.current?.click()}
            data-testid="prod-img-dropzone"
            className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition ${uploading ? "border-[#004BCA] bg-[#004BCA]/5" : "border-slate-200 hover:border-[#004BCA] hover:bg-slate-50"}`}
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-2 text-[#004BCA] font-medium text-sm">
                <Loader2 size={18} className="animate-spin"/> Uploading…
              </div>
            ) : (
              <>
                <Upload size={22} className="mx-auto text-slate-400 mb-2" strokeWidth={1.5}/>
                <div className="text-sm font-medium text-slate-700">Click to upload images</div>
                <div className="text-xs text-slate-500 mt-1">PNG, JPG, WebP up to 8 MB · multi-select supported</div>
              </>
            )}
            <input
              ref={fileRef}
              data-testid="prod-img-file"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              multiple
              hidden
              onChange={onFiles}
            />
          </div>

          {/* Optional URL paste fallback */}
          <details className="mt-3">
            <summary className="text-xs text-slate-500 cursor-pointer select-none">Or paste image URL</summary>
            <div className="flex gap-2 mt-2">
              <input data-testid="prod-img-url" value={imgUrl} onChange={e=>setImgUrl(e.target.value)} placeholder="https://…" className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm"/>
              <button type="button" data-testid="prod-img-add" onClick={addImage} className="btn-primary-sf px-4 py-2 rounded-xl inline-flex items-center gap-1 text-sm"><Plus size={14}/>Add</button>
            </div>
          </details>

          {form.images.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {form.images.map((u, i) => (
                <div key={i} data-testid={`prod-img-${i}`} className="relative w-24 h-28 rounded-xl overflow-hidden border border-slate-200 group">
                  <img src={u} alt="" className="w-full h-full object-cover"/>
                  <button type="button" data-testid={`prod-img-rm-${i}`} onClick={()=>rmImage(i)} className="absolute top-1 right-1 bg-white/95 rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition">
                    <X size={12}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <Toggle k="is_trending" form={form} set={set} label="Trending"/>
          <Toggle k="is_bestseller" form={form} set={set} label="Best Seller"/>
          <Toggle k="is_new" form={form} set={set} label="New Arrival"/>
        </div>

        {/* ─────── Virtual Try-On section ─────── */}
        <TryOnSection form={form} set={set} />

        <button data-testid="prod-save" className="btn-primary-sf px-6 py-2.5 rounded-full font-semibold">Save Product</button>
      </form>
    </div>
  );
}

// ─────── Try-On admin sub-section ───────
function TryOnSection({ form, set }) {
  const [allProducts, setAllProducts] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/products?limit=200").then(r => setAllProducts(r.data));
  }, []);

  const selectedRecs = useMemo(
    () => (form.recommended_product_ids || []).map(id => allProducts.find(p => p.product_id === id)).filter(Boolean),
    [form.recommended_product_ids, allProducts]
  );

  const toggleRec = (pid) => {
    const current = form.recommended_product_ids || [];
    const next = current.includes(pid) ? current.filter(i => i !== pid) : [...current, pid];
    set("recommended_product_ids", next);
  };

  const filtered = q
    ? allProducts.filter(p => p.title.toLowerCase().includes(q.toLowerCase()) || p.category.toLowerCase().includes(q.toLowerCase()))
    : allProducts;

  return (
    <div className="border-t border-slate-100 pt-5 mt-3 space-y-4" data-testid="tryon-admin-section">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-[#FF4500]"/>
        <h3 className="font-display font-bold">Virtual Try-On</h3>
      </div>

      <label className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 cursor-pointer">
        <input data-testid="prod-tryon-enabled" type="checkbox" checked={!!form.try_on_enabled} onChange={e=>set("try_on_enabled", e.target.checked)}/>
        <div className="flex-1">
          <div className="text-sm font-semibold">Enable Try-On for this product</div>
          <div className="text-xs text-slate-500">Shows the TRY IT ON button on this product page.</div>
        </div>
      </label>

      {form.try_on_enabled && (
        <>
          <label className="block">
            <span className="block text-xs font-semibold uppercase text-slate-500 mb-1">Product Type</span>
            <select data-testid="prod-type" value={form.product_type} onChange={e=>set("product_type", e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm capitalize">
              {PRODUCT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="block text-xs font-semibold uppercase text-slate-500 mb-1">Try-On Asset URL</span>
            <input data-testid="prod-tryon-asset" value={form.try_on_asset || ""} onChange={e=>set("try_on_asset", e.target.value)}
              placeholder="Auto-uses first product image if empty"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm"/>
            <span className="text-[11px] text-slate-500 mt-1 block">Optional. Provide a garment cutout for best AI results (transparent PNG recommended).</span>
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase text-slate-500">Recommended Products <span className="text-slate-400 normal-case">(shown in Complete Your Look)</span></span>
              <button type="button" data-testid="prod-recs-open" onClick={()=>setPickerOpen(true)} className="text-xs text-[#004BCA] font-semibold inline-flex items-center gap-1"><Plus size={12}/>Pick from catalog</button>
            </div>
            {selectedRecs.length === 0 ? (
              <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-500 text-center">No recommendations picked. Customers will see same-category items as a fallback.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selectedRecs.map(r => (
                  <div key={r.product_id} data-testid={`rec-pill-${r.product_id}`} className="inline-flex items-center gap-2 bg-slate-100 rounded-full pl-1 pr-2 py-1">
                    <img src={r.images?.[0]} className="w-6 h-6 rounded-full object-cover"/>
                    <span className="text-xs font-medium">{r.title}</span>
                    <button type="button" onClick={()=>toggleRec(r.product_id)} className="text-slate-500 hover:text-red-600"><X size={12}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Product picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={()=>setPickerOpen(false)} data-testid="rec-picker-modal">
          <div onClick={e=>e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center gap-2">
              <Search size={16} className="text-slate-400"/>
              <input data-testid="rec-picker-search" autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder="Search products by title or category…" className="flex-1 outline-none text-sm"/>
              <button type="button" onClick={()=>setPickerOpen(false)} className="p-1 hover:bg-slate-100 rounded-full"><X size={16}/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {filtered.map(p => {
                const picked = (form.recommended_product_ids || []).includes(p.product_id);
                return (
                  <button key={p.product_id} type="button" data-testid={`rec-pick-${p.product_id}`} onClick={()=>toggleRec(p.product_id)}
                    className={`relative text-left border rounded-xl p-2 hover:shadow-sm ${picked?"border-[#004BCA] bg-[#004BCA]/5":"border-slate-100"}`}>
                    <img src={p.images?.[0]} className="w-full aspect-square rounded-lg object-cover"/>
                    <div className="text-xs font-medium mt-2 truncate">{p.title}</div>
                    <div className="text-[10px] text-slate-500">{inr(p.price)} · {p.category}</div>
                    {picked && <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#004BCA] text-white grid place-items-center"><Check size={12}/></span>}
                  </button>
                );
              })}
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end">
              <button type="button" data-testid="rec-picker-done" onClick={()=>setPickerOpen(false)} className="btn-primary-sf px-5 py-2 rounded-full text-sm font-semibold">Done · {(form.recommended_product_ids||[]).length} selected</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function Input({label,type="text",value,onChange,testid}){return <label className="block"><span className="block text-xs font-semibold uppercase text-slate-500 mb-1">{label}</span><input data-testid={testid} type={type} value={value} onChange={e=>onChange(e.target.value)} required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm"/></label>;}
function Textarea({label,value,onChange,testid}){return <label className="block"><span className="block text-xs font-semibold uppercase text-slate-500 mb-1">{label}</span><textarea data-testid={testid} value={value} onChange={e=>onChange(e.target.value)} rows={3} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm"/></label>;}
function Pills({label,items,input,setInput,add,remove,testid}){return <div><span className="block text-xs font-semibold uppercase text-slate-500 mb-1">{label}</span><div className="flex gap-2 mb-2"><input data-testid={`${testid}-input`} value={input} onChange={e=>setInput(e.target.value)} className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2"/><button type="button" data-testid={`${testid}-add`} onClick={add} className="btn-primary-sf px-4 py-2 rounded-xl"><Plus size={14}/></button></div><div className="flex flex-wrap gap-2">{items.map((s,i)=><span key={i} className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-3 py-1 text-xs">{s}<button type="button" onClick={()=>remove(i)}><X size={12}/></button></span>)}</div></div>;}
function Toggle({k,form,set,label}){return <label className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2.5 cursor-pointer"><input data-testid={`prod-${k}`} type="checkbox" checked={!!form[k]} onChange={e=>set(k,e.target.checked)}/><span className="text-sm font-medium">{label}</span></label>;}
